import * as React from 'react';
import * as styles from '../components/styles.scss';
import type { IAutofill } from '@fluentui/react/lib/Autofill';
import type { IBaseFloatingPicker } from '@fluentui/react/lib/FloatingPicker';
import { Spinner } from '@fluentui/react/lib/Spinner';
import type { TimeZoneSuggestionType } from 'owa-time-zone-picker';
import { usePatchPartialPayload, setAnnouncement } from 'owa-timestream-asset-sdk';
import { useClockPayload } from '../hooks/useClockPayload';
import type { TimeZoneInfo } from '../schema';
import loc from 'owa-localize';
import { findLocationInfo_Searching } from 'owa-locstrings/lib/strings/findlocationinfo_searching.locstring.json';
import { addACityPlaceholder, timeZoneAddedAnnouncement } from '../clockStrings.locstring.json';

export default function useClockViewPickerViewState() {
    const [inputElement, setInputElement] = React.useState<HTMLInputElement | null>(null);
    const input = React.useRef<IAutofill | null>(null);
    const picker = React.useRef<IBaseFloatingPicker | null>(null);
    const patchPartialPayload = usePatchPartialPayload();
    const payload = useClockPayload();

    const onInputValueChange = React.useCallback((newValue?: string, composing?: boolean) => {
        picker.current?.showPicker();
        picker.current?.onQueryStringChanged(newValue ?? '');
    }, []);

    const onSuggestionSelected = React.useCallback(
        (newValue: TimeZoneSuggestionType) => {
            const oldTimeZones: TimeZoneInfo[] = [...payload.timeZones];
            oldTimeZones.push(newValue);
            patchPartialPayload({ timeZones: oldTimeZones });
            input.current?.clear();
            setAnnouncement(loc(timeZoneAddedAnnouncement, newValue.city));
        },
        [payload.timeZones]
    );

    const renderLoadingIndicator = React.useCallback(() => {
        return (
            <div className={styles.searchLoading}>
                <Spinner
                    label={loc(findLocationInfo_Searching, input.current?.value ?? '')}
                    labelPosition="right"
                />
            </div>
        );
    }, []);

    React.useEffect(() => {
        if (input.current?.inputElement) {
            setInputElement(input.current!.inputElement);
        }
    }, []);

    return {
        inputProps: {
            componentRef: input,
            onInputValueChange,
            placeholder: loc(addACityPlaceholder),
        },
        pickerProps: {
            componentRef: picker,
            onChange: onSuggestionSelected,
            inputElement: inputElement,
            renderLoadingIndicator,
        },
    };
}
